import { useCallback, useState } from 'react'

import { downloadBlob } from 'modules/core/lib/download'
import i18n from 'modules/core/lib/i18n'
import { apiClient } from 'modules/core/lib/mobx-rest'

import { TrainingMembership } from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/types'

const downloadStatus = {
  idle: 'idle',
  downloading: 'downloading',
  noCertificates: 'no_certificates',
  error: 'error',
} as const

type DownloadStatus = (typeof downloadStatus)[keyof typeof downloadStatus]

export const useCertificateDownload = (trainingMemberships: TrainingMembership[]) => {
  const [status, setStatus] = useState<DownloadStatus>(downloadStatus.idle)

  const getDocumentIdsForEmployees = useCallback(
    (employeeIds: number[]): number[] => {
      return trainingMemberships
        .filter((member) => member.employee?.id && employeeIds.includes(member.employee.id))
        .flatMap((member) => member.trainingMembershipFiles.map((file) => file?.documentId))
        .filter(Boolean)
    },
    [trainingMemberships]
  )

  const downloadCertificates = useCallback(
    async (employeeIds: number[]) => {
      const documentIds = getDocumentIdsForEmployees(employeeIds)

      if (documentIds.length === 0) {
        setStatus(downloadStatus.noCertificates)
        return
      }

      setStatus(downloadStatus.downloading)

      try {
        const response = await apiClient().post(
          '/documents/download_documents',
          { ids: documentIds.join(','), flat: true },
          { responseType: 'blob' }
        )

        if (!response || response.size === 0) {
          setStatus(downloadStatus.error)
          return
        }

        const blob = new Blob([response], { type: 'application/zip' })
        downloadBlob(
          blob,
          `${i18n.t('trainings.trainings_revamp.training_detail.employees_table.certificates')}.zip`
        )
        setStatus(downloadStatus.idle)
      } catch {
        setStatus(downloadStatus.error)
      }
    },
    [getDocumentIdsForEmployees]
  )

  const resetStatus = useCallback(() => setStatus(downloadStatus.idle), [])

  return {
    downloadCertificates,
    resetStatus,
    isDownloading: status === downloadStatus.downloading,
    hasNoCertificates: status === downloadStatus.noCertificates,
    hasError: status === downloadStatus.error,
  }
}
